import { Head, Link } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import sigilBlue from '../../images/SigilBlue.png';

interface Props {
    hasGameAccess: boolean;
}

/**
 * Star data for the twinkling animation.
 */
interface Star {
    x: number;
    y: number;
    size: number;
    opacity: number;
    twinkleSpeed: number;
    twinkleOffset: number;
}

/**
 * StarryBackground component - renders animated twinkling stars and nebula effects.
 * Uses canvas for performant star rendering with CSS for nebula gradients.
 */
function StarryBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const animationRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            generateStars();
        };

        const generateStars = () => {
            const starCount = Math.floor((canvas.width * canvas.height) / 3000);
            starsRef.current = [];

            for (let i = 0; i < starCount; i++) {
                starsRef.current.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5,
                    opacity: Math.random() * 0.5 + 0.3,
                    twinkleSpeed: Math.random() * 0.003 + 0.001,
                    twinkleOffset: Math.random() * Math.PI * 2,
                });
            }
        };

        const animate = (time: number) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            starsRef.current.forEach((star) => {
                const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
                const currentOpacity = star.opacity + twinkle * 0.3;

                ctx.beginPath();
                ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0.1, Math.min(1, currentOpacity))})`;
                ctx.fill();

                if (star.size > 1.5) {
                    ctx.beginPath();
                    ctx.arc(star.x, star.y, star.size * 2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(200, 220, 255, ${currentOpacity * 0.2})`;
                    ctx.fill();
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationRef.current);
        };
    }, []);

    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950" />
            <div className="absolute inset-0">
                <div
                    className="absolute top-0 right-0 w-[60%] h-[50%] opacity-30"
                    style={{
                        background: 'radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute bottom-0 left-0 w-[50%] h-[40%] opacity-25"
                    style={{
                        background: 'radial-gradient(ellipse at 30% 80%, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute top-1/3 left-1/4 w-[40%] h-[30%] opacity-20"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.3) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 80%)',
                    }}
                />
                <div
                    className="absolute bottom-1/4 right-1/4 w-[30%] h-[25%] opacity-15"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(20, 184, 166, 0.3) 0%, transparent 70%)',
                    }}
                />
            </div>
            <canvas ref={canvasRef} className="absolute inset-0" />
        </div>
    );
}

/**
 * MoonLogo component - renders a moon with the Sellune sigil overlaid.
 * Uses Libre Baskerville font for the title text.
 */
function MoonLogo() {
    return (
        <div className="relative w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56">
            {/* Outer glow effect */}
            <div
                className="absolute inset-0 rounded-full blur-3xl opacity-40"
                style={{
                    background: 'radial-gradient(circle, rgba(200, 210, 255, 0.6) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 70%)',
                }}
            />

            {/* Moon SVG */}
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full drop-shadow-2xl">
                <defs>
                    {/* Moon surface gradient */}
                    <radialGradient id="moonGradient" cx="40%" cy="40%" r="60%">
                        <stop offset="0%" stopColor="#E8E8F0" />
                        <stop offset="50%" stopColor="#C8C8D8" />
                        <stop offset="100%" stopColor="#A0A0B8" />
                    </radialGradient>

                    {/* Atmospheric glow */}
                    <radialGradient id="atmosphereGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="70%" stopColor="transparent" />
                        <stop offset="90%" stopColor="rgba(180, 190, 255, 0.2)" />
                        <stop offset="100%" stopColor="rgba(140, 150, 220, 0.4)" />
                    </radialGradient>
                </defs>

                {/* Main moon circle */}
                <circle cx="100" cy="100" r="85" fill="url(#moonGradient)" />

                {/* Crater details - subtle dark spots */}
                <circle cx="70" cy="60" r="12" fill="rgba(120, 120, 140, 0.2)" />
                <circle cx="130" cy="80" r="8" fill="rgba(120, 120, 140, 0.15)" />
                <circle cx="90" cy="120" r="15" fill="rgba(120, 120, 140, 0.18)" />
                <circle cx="50" cy="100" r="6" fill="rgba(120, 120, 140, 0.12)" />
                <circle cx="140" cy="130" r="10" fill="rgba(120, 120, 140, 0.14)" />
                <circle cx="110" cy="50" r="5" fill="rgba(120, 120, 140, 0.1)" />

                {/* Atmospheric glow ring */}
                <circle cx="100" cy="100" r="88" fill="none" stroke="url(#atmosphereGlow)" strokeWidth="8" />
            </svg>

            {/* Sigil overlay - centered on moon */}
            <div className="absolute inset-0 flex items-center justify-center">
                <img
                    src={sigilBlue}
                    alt="Sellune Sigil"
                    className="w-[150%] h-[150%] object-cover opacity-90 drop-shadow-lg"
                    style={{
                        filter: 'drop-shadow(0 0 20px rgba(100, 150, 255, 0.5))',
                    }}
                />
            </div>

            {/* Title below the moon - using Libre Baskerville font */}
            <div className="absolute -bottom-2 sm:-bottom-4 left-0 right-0 text-center">
                <h2
                    className="text-xl sm:text-2xl lg:text-3xl font-normal text-white/90 tracking-[0.2em] sm:tracking-[0.3em] drop-shadow-lg"
                    style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                    SELLUNE
                </h2>
            </div>
        </div>
    );
}

/**
 * Book icon for Tome
 */
function BookIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            <path d="M8 7h8" />
            <path d="M8 11h6" />
        </svg>
    );
}

/**
 * Newspaper icon for News
 */
function NewspaperIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
            <path d="M18 14h-8" />
            <path d="M15 18h-5" />
            <path d="M10 6h8v4h-8V6Z" />
        </svg>
    );
}

/**
 * Map/Game icon
 */
function MapIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
            <line x1="9" y1="3" x2="9" y2="18" />
            <line x1="15" y1="6" x2="15" y2="21" />
        </svg>
    );
}

interface ChoiceCardProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    href: string;
    available: boolean;
    color: 'purple' | 'amber' | 'blue';
}

/**
 * A large clickable card for navigation choices.
 * Shows "Chamber Not Yet Available" modal when clicking unavailable cards.
 */
function ChoiceCard({ title, description, icon, href, available, color }: ChoiceCardProps) {
    const [showModal, setShowModal] = useState(false);

    const colorClasses = {
        purple: {
            border: 'border-purple-500/30 hover:border-purple-400/50',
            bg: 'bg-purple-500/10',
            iconBg: 'bg-purple-500/20',
            iconColor: 'text-purple-400',
            glow: 'rgba(139, 92, 246, 0.3)',
        },
        amber: {
            border: 'border-amber-500/30 hover:border-amber-400/50',
            bg: 'bg-amber-500/10',
            iconBg: 'bg-amber-500/20',
            iconColor: 'text-amber-400',
            glow: 'rgba(245, 158, 11, 0.3)',
        },
        blue: {
            border: 'border-blue-500/30 hover:border-blue-400/50',
            bg: 'bg-blue-500/10',
            iconBg: 'bg-blue-500/20',
            iconColor: 'text-blue-400',
            glow: 'rgba(59, 130, 246, 0.3)',
        },
    };

    const styles = colorClasses[color];

    const cardContent = (
        <div
            className={`relative overflow-hidden rounded-2xl border ${styles.border} ${styles.bg} backdrop-blur-sm p-8 transition-all duration-300 cursor-pointer hover:scale-[1.02] hover:shadow-2xl`}
            style={{ boxShadow: `0 0 60px ${styles.glow}` }}
        >
            {/* Icon */}
            <div className={`w-24 h-24 mx-auto mb-6 rounded-2xl ${styles.iconBg} flex items-center justify-center`}>
                <div className={`w-14 h-14 ${styles.iconColor}`}>
                    {icon}
                </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-white text-center mb-3">
                {title}
            </h2>

            {/* Description */}
            <p className="text-gray-300 text-center text-lg">
                {description}
            </p>
        </div>
    );

    // Modal for unavailable chambers
    const modal = showModal && (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
        >
            <div
                className="relative bg-black border-2 border-amber-500 rounded-lg p-8 max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                style={{ boxShadow: '0 0 30px rgba(245, 158, 11, 0.3)' }}
            >
                {/* Close button */}
                <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-amber-500 hover:text-amber-400 transition-colors"
                    aria-label="Close"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Modal content */}
                <div className="text-center">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        Chamber Not Yet Available
                    </h3>
                    <p className="text-white text-lg">
                        This chamber is not yet open to you. Please check back later.
                    </p>
                </div>
            </div>
        </div>
    );

    if (available) {
        return (
            <Link href={href} className="block">
                {cardContent}
            </Link>
        );
    }

    return (
        <>
            <div className="block" onClick={() => setShowModal(true)}>
                {cardContent}
            </div>
            {modal}
        </>
    );
}

/**
 * Choose page - Main landing page after login.
 *
 * Displays three navigation options:
 * - Tome: Access to the knowledge archive (feature-gated)
 * - News: The Feywild Times newspaper (always available)
 * - Game: The hex map and gameplay (feature-gated)
 */
export default function Choose({ hasGameAccess }: Props) {
    return (
        <>
            <Head title="Choose Your Path" />

            <StarryBackground />

            <div className="relative min-h-screen flex items-center justify-center p-4 py-8">
                <div className="max-w-6xl w-full">
                    {/* Header with Moon Logo */}
                    <div className="text-center mb-12">
                        <div className="flex justify-center mb-8">
                            <MoonLogo />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                            Choose Your Path
                        </h1>
                        <p className="text-lg text-gray-300">
                            Where would you like to go?
                        </p>
                    </div>

                    {/* Choice Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        <ChoiceCard
                            title="Tome"
                            description="The archives of knowledge and lore"
                            icon={<BookIcon className="w-full h-full" />}
                            href="/tome"
                            available={hasGameAccess}
                            color="purple"
                        />

                        <ChoiceCard
                            title="News"
                            description="Read the latest Feywild Times"
                            icon={<NewspaperIcon className="w-full h-full" />}
                            href="/newspaper"
                            available={true}
                            color="amber"
                        />

                        <ChoiceCard
                            title="Game"
                            description="Enter the world and claim your hex"
                            icon={<MapIcon className="w-full h-full" />}
                            href="/map"
                            available={hasGameAccess}
                            color="blue"
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
